import React, { useEffect, useRef } from "react"
import ReactQuill, { Quill } from "react-quill-new"
import "react-quill-new/dist/quill.snow.css"
import { toast } from "react-toastify"
import { ApiuploadDocuments } from "../../../apiService/Core/Apicall.tsx"
import { FormatAlignCenter, FormatAlignJustify, FormatAlignLeft, FormatAlignRight, ImageOutlined } from "@mui/icons-material"




const fileupload = async (filePath: File, fileType: string): Promise<string> => {
  const userData = JSON.parse(localStorage.getItem("userData") || "{}")
  const formData = new FormData()

  formData.append("userId", userData.userId)
  formData.append("fileType", fileType)
  formData.append("file", filePath)

  try {
    const response: any = await ApiuploadDocuments(formData)
    if (response.status) {
      return response.location
    }
    throw new Error("Upload failed")
  } catch (error) {
    console.log(error)
    throw error
  }
}





const Editor = ({
  value,
  setValue,
  isUpdate
}: {
  value: string
  setValue: (v: string) => void
  isUpdate: boolean
}) => {
  const quillRef = useRef(null)
  const fileInputRef = useRef(null)
  const isProcessingImage = useRef(false) // Prevent race conditions



  const Font = Quill.import("formats/font") as any
  Font.whitelist = ["sans-serif", "serif", "monospace", "arial", "times-new-roman", "courier-new"]
  Quill.register(Font, true)

  const Size = Quill.import("formats/size") as any
  Size.whitelist = ["10px", "12px", "14px", "16px", "18px", "24px", "32px"]
  Quill.register(Size, true)


  /** Trigger file input */
  const handleImageClick = () => {
    fileInputRef.current?.click()
  }
  const encodeImageUrl = (url: string): string => {
    return url.replace(/ /g, '%20')
  }
  /** Handle file input change */
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0]
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file")
      return
    }

    try {
      const imageUrl = await fileupload(file, "project_static_files")
      const quill = quillRef.current?.getEditor()
      if (!quill) return

      const range = quill.getSelection(true)
      const index = range ? range.index : quill.getLength()
      const encodedUrl = encodeImageUrl(imageUrl)
      quill.insertEmbed(index, "image", encodedUrl)
      quill.insertText(index + 1, "\n")
      quill.setSelection(index + 2, 0)
    } catch (error) {
      console.error(error)
    } finally {
      event.target.value = ""
    }
  }

  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return

    /* ---------- HANDLE PASTE IMAGE UPLOAD ---------- */
    const handlePaste = async (e: ClipboardEvent) => {
      const items = e.clipboardData?.items
      if (!items) return

      const imageItem = Array.from(items).find(item =>
        item.type.startsWith("image/")
      )

      if (!imageItem) return

      // ✅ Prevent Quill's default paste behavior for images
      e.preventDefault()
      e.stopPropagation()

      if (isProcessingImage.current) return
      isProcessingImage.current = true

      try {
        const file = imageItem.getAsFile()
        if (!file) return
        const imageUrl = await fileupload(file, "project_static_files")
        const range = quill.getSelection(true)
        const index = range ? range.index : quill.getLength()
        const encodedUrl = encodeImageUrl(imageUrl)

        quill.insertEmbed(index, "image", encodedUrl)
        quill.insertText(index + 1, "\n")
        quill.setSelection(index + 2, 0)
      } catch (error) {
        console.error(error)
      } finally {
        isProcessingImage.current = false
      }
    }

    /* ---------- HANDLE DROP ---------- */
    const handleDrop = async (e: DragEvent) => {
      if (!e.dataTransfer?.files?.length) return

      const file = e.dataTransfer.files[0]
      if (!file.type.startsWith("image/")) return

      // ✅ Prevent Quill's default drop behavior for images
      e.preventDefault()
      e.stopPropagation()

      if (isProcessingImage.current) return
      isProcessingImage.current = true

      try {
        const imageUrl = await fileupload(file, "project_static_files")
        const range = quill.getSelection(true)
        const index = range ? range.index : quill.getLength()
        const encodedUrl = encodeImageUrl(imageUrl)

        quill.insertEmbed(index, "image", encodedUrl)
        quill.insertText(index + 1, "\n")
        quill.setSelection(index + 2, 0)
      } catch (error) {
        console.error(error)
      } finally {
        isProcessingImage.current = false
      }
    }

    /* ---------- CLEAN UP BASE64 IMAGES ---------- */
    const cleanupBase64Images = () => {
      const delta = quill.getContents()
      let modified = false
      const newOps = delta.ops.filter((op: any) => {
        if (op.insert?.image && op.insert.image.startsWith("data:image")) {
          modified = true
          return false // Remove base64 images
        }
        return true
      })

      if (modified) {
        quill.setContents({ ops: newOps })
      }
    }

    // Add event listeners with capture phase to intercept before Quill
    const editorElement = quill.root
    editorElement.addEventListener("paste", handlePaste, true) // ✅ Capture phase
    editorElement.addEventListener("drop", handleDrop, true)   // ✅ Capture phase

    // Periodically clean up any base64 images that slip through
    const cleanupInterval = setInterval(cleanupBase64Images, 500)

    return () => {
      editorElement.removeEventListener("paste", handlePaste, true)
      editorElement.removeEventListener("drop", handleDrop, true)
      clearInterval(cleanupInterval)
    }
  }, [])


  const handleChange = (content: string) => {
    if (content === "<p><br></p>") {
      setValue("") // treat as empty
    } else {
      console.log("Values", content)
      setValue(content)
    }
  }

  const handleAlign = (alignment: string) => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    quill.format("align", alignment || false)
  }

  return (
    <div>
      {/* Custom Toolbar */}
      <div id="editor-toolbar">
        <button className="ql-bold" />
        <button className="ql-italic" />
        <button className="ql-underline" />
        <button className="ql-link" />
        <button type="button" onClick={handleImageClick}><ImageOutlined /></button>
        <button className="ql-clean" />
        <button type="button" onClick={() => handleAlign("")} title="Align Left">
          <FormatAlignLeft />
        </button>
        <button type="button" onClick={() => handleAlign("center")} title="Align Center">
          <FormatAlignCenter />
        </button>
        <button type="button" onClick={() => handleAlign("right")} title="Align Right">
          <FormatAlignRight />
        </button>
        <button type="button" onClick={() => handleAlign("justify")} title="Justify">
          <FormatAlignJustify />
        </button>
        <button className="ql-list" value="ordered" />
        <button className="ql-list" value="bullet" />
        <select className="ql-color" />
        <select className="ql-background" />
        <select className="ql-size" defaultValue="14px">
          <option value="10px">10</option>
          <option value="12px">12</option>
          <option value="14px">14</option>
          <option value="16px">16</option>
          <option value="18px">18</option>
          <option value="24px">24</option>
          <option value="32px">32</option>
        </select>
        <select className="ql-font" defaultValue="sans-serif">
          <option value="sans-serif">Sans Serif</option>
          <option value="serif">Serif</option>
          <option value="monospace">Monospace</option>
          <option value="arial">Arial</option>
          <option value="times-new-roman">Times New Roman</option>
          <option value="courier-new">Courier New</option>
        </select>

      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileChange}
      />

      <ReactQuill
        ref={quillRef}
        value={value}
        // onChange={setValue}
        onChange={handleChange}
        readOnly={isUpdate}
        modules={{ toolbar: { container: "#editor-toolbar" } }}
        style={{ height: 200 }}
      />
    </div>
  )
}

export default Editor
